// RankPage.jsx
/**
 * RankPage
 * --------------------------------------------------------------------
 * 목적:
 *  - 포인트/방문 정보 기반 "단골 손님 랭킹 상세" 페이지의 컨테이너.
 *  - 헤더(좌: 언어/기간 드롭다운, 우: 달력), 랭킹 테이블(UserRankDetail),
 *    포인트 상세 모달(PointDetails), 하단 내비게이션(BottomNavbar)까지
 *    하나의 라우트에서 오케스트레이션.
 *
 * 데이터 흐름:
 *  - useRankingData 훅에서 랭킹 원본 데이터, 유효 날짜 목록, 매장 ID, 로딩 상태 등 수집.
 *  - CalendarContent에서 날짜 선택 → Redux thunk(fetchRanking) 디스패치 → 스토어 갱신.
 *  - UserRankDetail은 (현재/지난 기간) 데이터를 받아 정렬, 증감 표시, 모달 열기 콜백 전달.
 *  - 모달은 선택된 사용자의 phone_number를 기반으로 PointDetails를 렌더링.
 *
 * 상태/동작:
 *  - page("daily" | "weekly" | "monthly"): 기간 탭.
 *  - pageType: 상세 유형(여기서는 "bestMenu"로 고정되어 사용).
 *  - leftSide/rightSide: 좌/우 사이드 패널(드롭다운/달력) 개폐 상태. 동시 열림 방지.
 *  - isModalOpen/selectedPhone: 포인트 상세 모달 상태/대상자.
 *
 * 라우팅:
 *  - useLocation.state?.page 로 진입 시 초기 탭 복원.
 *  - goBack은 navigate(-1)로 브라우저 히스토리 기반 뒤로가기.
 *
 * 접근성/UX:
 *  - ReactModal 사용(ariaHideApp=false). 오버레이/콘텐츠 스타일 커스텀.
 *  - 첫 마운트/탭 복원 시 window.scroll(0, 0)로 상단 고정(스크롤 유지 정책이 다르면 조정 필요).
 *
 */

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactModal from "react-modal";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Header from "../../component/common/Header";
import DropDownMenu from "../../component/common/DropDownMenu";
import CalendarContent from "../../component/common/CalendarContent";
import BottomNavbar from "../../component/common/BottomNavbar";
import LoadingScreen from "../../component/common/LoadingScreen";
import UserRankDetail from "../../component/report/UserRankDetail";
import PointDetails from "../../pages/PointPages/PointDetails";
import useRankingData from "../../hooks/useRankingData";
import { fetchRanking } from "../../store/slices/rankingSlice";
import { isTokenExpired } from "../../functions/CheckToken";

export default function RankPage() {
  const location = useLocation(); // 라우팅 시 전달된 state(예: page 탭) 접근
  const navigate = useNavigate(); // 뒤로가기/로그인 이동 등 라우팅 제어
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(); // 다국어 처리 훅

  // 기간 탭: 기본은 'daily'
  const [page, setPage] = useState("daily");
  // 상세 유형(현재는 'bestMenu'만 사용하지만, UI 구조상 타입 분기 대비)
  const [pageType, setPageType] = useState("");
  // 좌/우 사이드 패널 상태(동시 열림 방지)
  const [leftSide, setLeftSide] = useState(false);
  const [rightSide, setRightSide] = useState(false);
  // 포인트 상세 모달 상태 및 대상 사용자(전화번호)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState("");

  // 드롭다운에서 사용할 라벨/매핑
  const pageList = ["daily", "weekly", "monthly"];
  // 현재 구조에서는 모두 'bestMenu'로 동일. 타입 확장 시 인덱스 기반 매핑 용도
  const pageTypeList = ["bestMenu", "bestMenu", "bestMenu"];
  // 좌측 드롭다운 라벨(예: "일간 되돌아보기"). i18n 키 조합으로 생성
  const sideList = [
    t("userRankDetail.daily") + t("userRankDetail.back"),
    t("userRankDetail.weekly") + t("userRankDetail.back"),
    t("userRankDetail.monthly") + t("userRankDetail.back"),
  ];

  // 랭킹/날짜/스토어/로딩 등 데이터는 커스텀 훅에서 제공
  const {
    currentDate, // 현재 기준일(예: 2025-08-11)
    rankDetailValue, // { daily: [...], weekly: [...], monthly: [...] } 형태 가정(현재 기간 데이터)
    lastDetailValue, // 직전 기간 데이터(증감 계산에 사용)
    isLoadingState, // 초기 로딩/재조회 로딩 상태
    storeId, // 현재 매장 식별자
    validDateList, // 달력에서 선택 가능한 유효 날짜 목록
  } = useRankingData();

  const { state } = location; // 라우팅 state 단축 참조

  // 1) 진입 시 액세스 토큰 검증 → 만료/없음이면 로그인으로
  useEffect(() => {
    const token = sessionStorage.getItem("accessToken");
    const is_expired = isTokenExpired(token);
    if (!token || is_expired === true) {
      // UX: 갑작스런 로그아웃 혼선을 줄이기 위한 알림
      window.alert("데이터 최신화를 위해 재로그인을 시도합니다.");
      navigate("/login");
      return;
    }
    // deps에 dispatch는 불필요하지만 린트 무시 대신 포함(부수효과 없음)
  }, [dispatch, navigate]);

  // 2) 라우팅으로 들어올 때 page 탭 복원 + 상단으로 스크롤 고정
  useEffect(() => {
    window.scroll(0, 0); // 필요 시 스크롤 복원 정책과 충돌 가능 → 요구사항에 맞게 조절
    if (location.state?.page) setPage(location.state.page);
  }, [location.state]);

  // 모달 열기: 대상 유저의 phone_number를 저장 후 오픈
  const openModalFor = (user) => {
    setSelectedPhone(user.phone_number);
    setIsModalOpen(true);
  };

  // 모달 닫기: 대상 정보 초기화(+ 닫기)
  const closeModal = () => {
    setSelectedPhone("");
    setIsModalOpen(false);
  };

  // 좌측 패널 토글: 우측이 열려 있으면 닫고 좌측 열기/닫기
  const openLeftSide = () => {
    if (rightSide) {
      setRightSide(false);
    }
    setLeftSide(!leftSide);
  };

  // 우측 패널 토글: 좌측이 열려 있으면 닫고 우측 열기/닫기
  const openRightSide = () => {
    if (leftSide) {
      setLeftSide(false);
    }
    setRightSide(!rightSide);
  };

  // 언어 전환: en↔ko 토글. 좌측 패널은 닫음
  const changeLanguage = (type) => {
    if (i18n.language === "en" && type === "ko") {
      i18n.changeLanguage("ko");
    } else if (i18n.language === "ko" && type === "en") {
      i18n.changeLanguage("en");
    }
    setLeftSide(false);
  };

  // 드롭다운 선택 처리: 인덱스 기반으로 page/pageType 동시 변경
  const handleDropDown = (index) => {
    setPageType(pageTypeList[index]);
    setPage(pageList[index % 3]);
  };

  // 로딩 상태에서는 스켈레톤/로딩 화면 우선 반환
  if (isLoadingState) {
    return (
      <LoadingScreen
        txt={
          <span>
            랭킹 데이터를 <br /> 분석하고 있어요!
          </span>
        }
        subtxt={
          <span style={{ fontSize: "2.5vw", color: "grey" }}>
            최대 30초까지 걸릴 수 있어요!
          </span>
        }
      ></LoadingScreen>
    );
  }

  // 실제 UI 렌더링
  return (
    <div className="block pt-5 overflow-x-hidden">
      {/* 헤더: 좌측/우측 패널 열기 콜백과 현재 페이지/날짜 전달 */}
      <Header
        leftSide={openLeftSide}
        t={t}
        i18n={i18n}
        rightSide={openRightSide}
        page={page}
        currentDate={currentDate}
      ></Header>

      {/* 좌측 슬라이드 패널(드롭다운): framer-motion으로 슬라이드 인/아웃 */}
      <motion.div
        className="fixed left-5 z-[9999]"
        initial={{ x: "-100%", opacity: 0 }}
        animate={{ x: leftSide ? 0 : "-100%", opacity: leftSide ? 1 : 0 }}
        exit={{ x: "-100%", opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* 열렸을 때만 실제 컴포넌트 마운트 → 불필요한 이벤트/포커스 방지 */}
        {leftSide && (
          <DropDownMenu
            changeLanguage={changeLanguage}
            sideList={sideList}
            open={openLeftSide}
            onclickFunction={handleDropDown}
            t={t}
            i18n={i18n}
          ></DropDownMenu>
        )}
      </motion.div>

      {/* 우측 슬라이드 패널(달력): 날짜 선택 시 fetchRanking 디스패치 */}
      <motion.div
        className="fixed right-5 z-[9999]"
        initial={{ x: "+100%", opacity: 0 }}
        animate={{ x: rightSide ? 0 : "+100%", opacity: rightSide ? 1 : 0 }}
        exit={{ x: "+100%", opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {rightSide && (
          <CalendarContent
            validDateList={validDateList}
            detail={pageType}
            page={page}
            currentDate={currentDate}
            open={openRightSide}
            t={t}
            i18n={i18n}
            onSelectDate={(date) => {
              // 선택 날짜 기준 랭킹 재조회 → 이후 스토어 값이 바뀌면 UserRankDetail이 새 데이터로 렌더
              dispatch(fetchRanking(date));
              openRightSide(); // 선택 후 패널 닫기
            }}
          ></CalendarContent>
        )}
      </motion.div>

      {/* 랭킹 상세 테이블: 현재/지난 기간 데이터 및 뷰어 콜백 전달 */}
      <UserRankDetail
        storeId={storeId}
        rankDetailValue={rankDetailValue}
        lastDetailValue={lastDetailValue}
        page={page}
        //goBack={() => navigate(`/${page}`)}
        goBack={() => navigate(-1)} // 브라우저 히스토리 기반 뒤로가기
        onView={openModalFor} // 행의 "조회" 버튼 클릭 → 모달 오픈
        t={t}
        i18n={i18n}
      />

      {/* 포인트 상세 모달: 선택한 사용자 전화번호를 전달 */}
      <ReactModal
        ariaHideApp={false} // 포털 루트가 애플리케이션 외부일 때 스크린리더 숨김 비활성화
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            width: "85vw",
            height: "75vh",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
          overlay: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)", // 배경 디머
          },
        }}
      >
        <div className="modal_content">
          {/* storeId/phoneNum 조합으로 사용자 포인트 상세 조회 */}
          <PointDetails storeId={storeId} phoneNum={selectedPhone} />

          {/* 닫기 버튼: 스타일 커스텀. 아래에 동일 기능 버튼이 하나 더 있어 중복임 */}
          <button
            onClick={closeModal}
            style={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "4vw",
              padding: "0.5em 1em",
            }}
          >
            닫기
          </button>

          <div className="modal_footer">
            <button onClick={closeModal} style={{ fontSize: "4vw" }}>
              닫기
            </button>
          </div>
        </div>
      </ReactModal>

      {/* 하단 내비게이션: 탭 전환 및 현재 날짜 전달 */}
      <BottomNavbar
        changeDetail={setPage} // 탭 전환 콜백
        page={page}
        pageType="userRank"
        info="userRank"
        currentDate={currentDate}
        t={t}
        i18n={i18n}
      />
    </div>
  );
}
